import * as React from 'react'
import { Col, Row } from 'reactstrap'
import { StepGuestsWalletScopes } from '@modules/reception/checkin/step-guests/step-guests-guest-form/wallet/scopes-list'
import { useFormContext, useWatch } from 'react-hook-form'
import { StepGuestsGuestFormInputs } from '@modules/reception/checkin/step-guests/step-guests-guest-form/models'
import { GuestWalletScope, ReceptionBookingGuest } from '@models/booking'
import { useResortWalletScope } from '@modules/reception/checkin/step-guests/step-guests-guest-form/wallet/use-resort-wallet-scope'
import { FormInput } from '@hyper/forms'

interface Props {
  guest: ReceptionBookingGuest | undefined
  resortId: number
}

export const StepGuestsGuestFormWallet = ({ guest, resortId }: Props): JSX.Element => {
  const {
    control,
    setValue,
    formState: { touchedFields },
  } = useFormContext<StepGuestsGuestFormInputs>()

  const guestType = useWatch({ control, name: 'type' })

  const availableScopes = useResortWalletScope(resortId)

  const defaultWalletLimits = React.useMemo(
    () =>
      availableScopes.reduce((acc, scope) => {
        const scopeData = DEFAULT_SCOPES.find(({ scope: defaultScope }) => defaultScope === scope)
        return scopeData ? { ...acc, [scope]: { enabled: true, limit: null, ...scopeData } } : acc
      }, {}),
    [guestType],
  )

  React.useEffect(() => {
    if (!guest?.id && !touchedFields.wallet_limits) {
      const isAdult = guestType.value === 'adult'

      setValue('wallet_limits', {
        ...defaultWalletLimits,
        global: { scope: null, limit: null, limit_total: isAdult ? null : 50, scope_display: '', enabled: false },
      })
    }
  }, [guestType])

  return (
    <>
      <Row className="mt-2">
        <Col md={12}>
          <strong>Dostęp do zakupów:</strong>
        </Col>
        <Col md={12} className="d-flex px-0 mt-2">
          <FormInput
            name="wallet_limits.global.limit"
            colSize={4}
            placeholder="np. 50 zł"
            label="Limit dzienny"
            formGroupClassName="mb-0"
            type="currency"
            setValue={setValue}
          />
          <FormInput
            name="wallet_limits.global.limit_total"
            colSize={4}
            label="Limit całkowity"
            placeholder="np. 100 zł"
            formGroupClassName="mb-0"
            type="currency"
            setValue={setValue}
          />
        </Col>
      </Row>
      <StepGuestsWalletScopes resortId={resortId} />
    </>
  )
}

interface DefaultScope {
  scope: GuestWalletScope
  scope_display: string
}

const DEFAULT_SCOPES: DefaultScope[] = [
  { scope: 'shop', scope_display: 'Sklep' },
  { scope: 'bistro', scope_display: 'Bistro' },
  { scope: 'cafe', scope_display: 'Kawiarnia' },
  { scope: 'sweet_corner', scope_display: 'Słodki Kącik' },
  { scope: 'beach_bar', scope_display: 'Beach Bar' },
  { scope: 'itinerant_trade', scope_display: 'Wózek' },
  { scope: 'vending_machines', scope_display: 'Automaty (Vendingowe)' },
  { scope: 'vending_machines_gastronomy', scope_display: 'Automaty (gastronomiczne)' },
  { scope: 'vending_machines_entertainment', scope_display: 'Automaty (rozrywkowe)' },
  { scope: 'vending_machines_parkfun', scope_display: 'Automaty Holiday Park & Fun' },
  { scope: 'tickets_parkfun', scope_display: 'Bilety Holiday Park & Fun' },
]
